import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ArrowRightIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import ChartSection from '../components/tokenomics/ChartSection';
import RevenueDistribution from '../components/tokenomics/RevenueDistribution';
import HowItWorks from '../components/tokenomics/HowItWorks';
import TokenDistribution from '../components/tokenomics/TokenDistribution';
import GrowingTogether from '../components/tokenomics/GrowingTogether';

export default function Tokenomics() {
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <>
      <Helmet>
        <title>RentFi | Invest in Real Estate & Earn Monthly Income</title>
        <meta 
          name="description" 
          content="Start investing in RentFi's real estate portfolio and earn up to 12.88% annual yields. Learn about RENT token distribution, revenue sharing, and how to generate passive income through property investments." 
        />
        <meta 
          name="keywords" 
          content="RentFi invest, RENT token, real estate investment, passive income, tokenomics, revenue distribution, property investment, blockchain real estate" 
        />
        <link rel="canonical" href="https://rentfi.io/invest" />
      </Helmet>
      <div className="min-h-screen bg-gray-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header Section */}
            <div className="text-center mb-16">
              <h1 className="text-primary mb-4">Generate Passive Income Through Real Estate</h1>
              <p className="text-body-large text-gray-600">
                Start earning monthly yields from professionally managed properties
              </p>
            </div>

            {/* Chart Section */}
            <ChartSection />

            {/* Revenue Distribution */}
            <RevenueDistribution />

            {/* Contract Information Section */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-16">
              <h2 className="text-primary mb-8 text-center">Contract Information</h2>
              <div className="max-w-2xl mx-auto">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-gray-900 mb-4 font-kanit text-[22px] leading-[36px]">Official Contract Address</h3>
                  <div className="flex items-center justify-between bg-white p-4 rounded-lg border border-gray-200">
                    <code className="text-gray-600 font-mono">EPW6L3UrX8ArF4SVsGFNNNs2XccugnJGNNzXLeHUCNuz</code>
                    <button
                      onClick={() => copyToClipboard('EPW6L3UrX8ArF4SVsGFNNNs2XccugnJGNNzXLeHUCNuz')}
                      className="text-secondary hover:text-secondary-dark p-2 rounded-lg transition-colors"
                      aria-label="Copy contract address to clipboard"
                    >
                      <DocumentDuplicateIcon className="w-5 h-5" />
                    </button>
                  </div>
                  <p className="text-gray-500 text-sm mt-2">Click the copy icon to copy the address to your clipboard</p>
                </div>
              </div>
            </div>

            {/* How It Works Section */}
            <HowItWorks />

            {/* Token Distribution */}
            <TokenDistribution />

            {/* Growing Together Section */}
            <GrowingTogether />

            {/* Ready to Invest Section */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-primary mb-4 text-center">Ready to Invest?</h2>
              <h5 className="text-secondary text-center mb-2">
                RENT - The Monthly Dividend Token
              </h5>
              <p className="text-body-large text-gray-900 text-center mb-8">
                Start earning passive income from real estate today
              </p>
              <div className="text-center">
                <a
                  href="https://raydium.io/swap/?inputMint=sol&outputMint=EPW6L3UrX8ArF4SVsGFNNNs2XccugnJGNNzXLeHUCNuz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-cta inline-flex items-center text-lg px-8 py-4 group"
                  aria-label="Start trading RENT tokens on Raydium decentralized exchange"
                  data-tracking="cta-click"
                  data-tracking-location="tokenomics-footer"
                  data-tracking-destination="raydium"
                >
                  Trade Now
                  <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}